import { type Currency } from '@principle-theorem/accounting';
import {
  AppointmentDeposit,
  DepositAmountResolver,
} from '@principle-theorem/principle-core';
import {
  type DepositBookingContext,
  type IDepositSettings,
  type IEvent,
  type ITreatmentTemplateWithStep,
} from '@principle-theorem/principle-core/interfaces';
import { type DayOfWeek } from '@principle-theorem/shared';
import {
  DepositAbsenceKind,
  type IDepositAbsence,
} from '../deposit-status-chip/deposit-chip-display';

/**
 * Everything a treatment option needs to say whether picking it will ask the
 * patient for a deposit. Booking timing is included because the same strategy
 * holds a card or charges it depending on how far ahead the booking is.
 */
export interface ITreatmentOptionDepositContext {
  readonly settings?: IDepositSettings;
  readonly bookingContext: DepositBookingContext;
  readonly patientDepositRequired?: boolean;
  readonly appointmentDay?: DayOfWeek;
  readonly appointmentStart?: IEvent['from'];
  readonly hasApprovableBookingRequest: boolean;
  readonly currency?: Currency;
}

/**
 * Previews the deposit a treatment option would require, for the selectors
 * that list treatments before one is chosen.
 *
 * Shared rather than duplicated per selector: the preview has to agree with
 * the deposit the save actually creates, so a second copy of this that drifted
 * would quietly promise a patient one amount and charge another.
 */
export class TreatmentOptionDeposits {
  /**
   * Keyed by treatment template id, and deliberately sparse — an option that
   * needs no deposit has no entry, so callers can render a chip only where
   * there is something to say.
   */
  static resolve(
    templates: readonly ITreatmentTemplateWithStep[],
    context: ITreatmentOptionDepositContext
  ): Map<string, IDepositAbsence> {
    return new Map(
      templates.flatMap((template) =>
        TreatmentOptionDeposits._resolveOne(template, context)
      )
    );
  }

  private static _resolveOne(
    template: ITreatmentTemplateWithStep,
    context: ITreatmentOptionDepositContext
  ): [string, IDepositAbsence][] {
    const templateId = template.step.template.ref.id;
    const resolution = DepositAmountResolver.resolve({
      settings: context.settings,
      treatmentTemplateId: templateId,
      bookingContext: context.bookingContext,
      appointmentDay: context.appointmentDay,
      patientDepositRequired: context.patientDepositRequired,
    });
    if (!resolution.required) {
      return [];
    }

    // Resolved the way the deposit request will be created, so the icon here
    // matches the chip shown once the item is chosen.
    const capture = AppointmentDeposit.getEffectiveCapture({
      captureStrategy: resolution.captureStrategy,
      appointmentStart: context.appointmentStart,
      hasApprovableBookingRequest: context.hasApprovableBookingRequest,
    });

    return [
      [
        templateId,
        {
          kind: DepositAbsenceKind.Required,
          amountCents: resolution.amount,
          currency: context.currency,
          paymentMode: capture.paymentMode ?? resolution.paymentMode,
          captureStrategy: capture.captureStrategy,
          source: resolution.source,
        },
      ],
    ];
  }
}
